import { Link } from 'react-router';
import { useTitle } from '../lib/hooks.js';
import { useMeta } from '../state/MetaContext.jsx';

const HowItWorks = () => {
  useTitle('How it works');
  const { platform } = useMeta();

  return (
    <div className="container section prose">
      <h1>How it works</h1>

      <h2>If you are looking for a house</h2>
      <ol>
        <li>
          <strong>Search.</strong> Start with the town, then narrow down by rent, number of rooms and the things that
          decide it for you: a private water supply, your own meter, a tarred road.
        </li>
        <li>
          <strong>Look carefully.</strong> Photos are on every listing and many landlords add a short video. Read the
          money-up-front table so the deposit does not surprise you.
        </li>
        <li>
          <strong>Ask questions.</strong> Message the landlord through the platform. You do not have to hand out your
          number before you are ready.
        </li>
        <li>
          <strong>Apply.</strong> Fill the form once, attach your ID and proof of income, and send it. You can apply for
          several houses without retyping anything.
        </li>
        <li>
          <strong>Watch the status.</strong> Submitted, under review, approved or rejected. You get a notification each
          time it changes.
        </li>
      </ol>

      <div className="notice notice-warn">
        <strong>Never pay before you see the house.</strong> No genuine landlord needs a deposit to unlock a viewing. If
        somebody asks, use the report link on the listing and we will look into it.
      </div>

      <h2 id="landlords">If you have a property to let</h2>
      <ol>
        <li>
          <strong>Create a landlord account.</strong> Add your business name if you let under one.
        </li>
        <li>
          <strong>Write the listing.</strong> Be specific about the neighbourhood, the water and light situation, and
          what the deposit is. Listings that answer the obvious questions get far fewer time-wasting calls.
        </li>
        <li>
          <strong>Add pictures and a video.</strong> Up to twenty files. You can upload a video or paste a YouTube link.
        </li>
        <li>
          <strong>Publish.</strong> New listings are checked by an administrator before they go live. That normally
          happens the same day.
        </li>
        <li>
          <strong>Work through the applications.</strong> Mark one under review while you check the person out, then
          approve or reject. Approving one applicant closes the others and marks the house as pending.
        </li>
        <li>
          <strong>Keep the status honest.</strong> Move a listing to Rented once the keys change hands. It keeps your
          phone quiet and keeps the marketplace trustworthy.
        </li>
      </ol>

      <h2>Verification</h2>
      <p>
        Landlords can upload a national ID or business registration under their profile. Once an administrator checks it,
        a verified badge appears on every listing they own. It is optional, but tenants notice it.
      </p>

      <h2>Money</h2>
      <p>
        Every amount on the platform is in XAF (FCFA). Rent is quoted per month and the deposit is quoted in months of
        rent, which is how it is normally agreed here. Online rent payment is not part of this release: rent and deposits
        are still settled directly between tenant and landlord.
      </p>

      <h2>Still stuck?</h2>
      <p>
        Write to {platform?.supportEmail ? <a href={`mailto:${platform.supportEmail}`}>{platform.supportEmail}</a> : 'support'}
        {platform?.supportPhone ? <> or call <a href={`tel:${platform.supportPhone}`}>{platform.supportPhone}</a></> : null}.
      </p>

      <p>
        <Link to="/search" className="btn btn-primary">
          Start searching
        </Link>
      </p>
    </div>
  );
};

export default HowItWorks;
